// Alta de cuenta con detección de parecidos en vivo: Guardar queda bloqueado
// hasta que la persona decide «usar esta cuenta» o «es distinta».
import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Boton, Campo, Input, Pill, PuntoNaranja, TituloPagina, useToast } from "@iavmx/ui";
import { ErrorApi, leer } from "../api";
import { ErrorEnLinea, Kicker, Miga } from "../componentes";
import { relativo } from "../formato";
import { useSesion } from "../sesion";
import type { Parecido } from "../tipos";

interface Formulario {
  razonSocial: string;
  nombreComercial: string;
  rfc: string;
  segmento: string;
  telefono: string;
  correo: string;
  domicilio: string;
  origen: string;
  contactoNombre: string;
  contactoPuesto: string;
  contactoTelefono: string;
  contactoCorreo: string;
}

const VACIO: Formulario = { razonSocial: "", nombreComercial: "", rfc: "", segmento: "", telefono: "", correo: "", domicilio: "", origen: "", contactoNombre: "", contactoPuesto: "", contactoTelefono: "", contactoCorreo: "" };
const RFC = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/i;

function useRetraso<T>(valor: T, ms: number): T {
  const [v, setV] = React.useState(valor);
  React.useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

function limpiar(f: Formulario) {
  const o = (s: string) => (s.trim() ? s.trim() : null);
  return { razonSocial: f.razonSocial.trim(), nombreComercial: o(f.nombreComercial), rfc: o(f.rfc), telefono: o(f.telefono), correo: o(f.correo), domicilio: o(f.domicilio), segmento: o(f.segmento), origen: o(f.origen) };
}

export function AltaCuenta() {
  const { api, listo } = useSesion();
  const navegar = useNavigate();
  const { avisar } = useToast();
  const [f, setF] = React.useState<Formulario>(VACIO);
  const [decision, setDecision] = React.useState<"pendiente" | "distinta">("pendiente");
  const [error, setError] = React.useState<unknown>(null);
  const pon = (k: keyof Formulario) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setF((v) => ({ ...v, [k]: e.target.value }));
    setDecision("pendiente");
  };

  const datos = limpiar(f);
  const clave = JSON.stringify([datos.razonSocial, datos.nombreComercial, datos.rfc, datos.telefono, datos.correo]);
  const claveRetrasada = useRetraso(clave, 350);
  const buscar = datos.razonSocial.length >= 3 || Boolean(datos.rfc) || Boolean(datos.telefono) || Boolean(datos.correo);
  const parecidos = useQuery({
    queryKey: ["parecidos", claveRetrasada],
    queryFn: () => api.api.cuentas.parecidos.$post({ json: limpiar(f) }).then((r) => leer<{ parecidos: Parecido[]; conEmbeddings: boolean }>(r)),
    enabled: listo && buscar && clave === claveRetrasada,
    placeholderData: (prev) => prev,
  });
  const lista = buscar ? (parecidos.data?.parecidos ?? []) : [];
  const bloquean = lista.filter((p) => p.nivel !== "posible");
  const rfcValido = !datos.rfc || RFC.test(datos.rfc);
  const rfcCoincide = lista.some((p) => p.motivos.includes("Mismo RFC"));
  const faltan = datos.razonSocial.length < 2 || !rfcValido;
  const bloqueado = faltan || (bloquean.length > 0 && decision !== "distinta") || rfcCoincide;
  const estadoGuardar = faltan ? "Falta la razón social." : bloquean.length && decision !== "distinta" ? "Se activa cuando resuelvas los parecidos." : rfcCoincide ? "Ese RFC ya está en otra cuenta." : decision === "distinta" ? "Lista para guardar como cuenta distinta." : "";

  const guardar = useMutation({
    mutationFn: async () => {
      const contacto = f.contactoNombre.trim()
        ? { nombre: f.contactoNombre.trim(), puesto: f.contactoPuesto.trim() || null, telefono: f.contactoTelefono.trim() || null, correo: f.contactoCorreo.trim() || null, principal: true }
        : null;
      const r = await api.api.cuentas.$post({ json: { ...datos, contacto, decision: decision === "distinta" ? "distinta" : undefined, parecidosRevisados: lista.map((p) => p.id) } });
      return leer<{ id: string }>(r);
    },
    onSuccess: ({ id }) => {
      avisar({ titulo: "Cuenta guardada", descripcion: decision === "distinta" ? "Quedó marcada como revisada distinta con tu nombre y fecha." : undefined, tono: "exito" });
      void navegar({ to: "/cuentas/$id", params: { id } });
    },
    onError: (e) => {
      setError(e);
      if (e instanceof ErrorApi && e.status === 409) setDecision("pendiente");
    },
  });

  return (
    <>
      <Miga partes={[{ texto: "Cuentas", to: "/cuentas" }, { texto: "nueva" }]} />
      <TituloPagina descripcion="Todo el equipo ve la prospección: si la cuenta ya existe, te lo decimos antes de guardar para no duplicar propuestas.">
        Alta de cuenta
        <PuntoNaranja />
      </TituloPagina>
      <div className="grid gap-8 pb-20 lg:grid-cols-[minmax(0,1fr)_420px] lg:pb-0">
        <form
          className="min-w-0 space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            if (!bloqueado) guardar.mutate();
          }}
          noValidate
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo etiqueta="Razón social" htmlFor="razon" className="sm:col-span-2">
              <Input id="razon" value={f.razonSocial} onChange={pon("razonSocial")} required autoFocus placeholder="Autos del Norte de Saltillo, S.A. de C.V." className="text-base sm:text-sm" />
            </Campo>
            <Campo etiqueta="Nombre comercial" htmlFor="comercial">
              <Input id="comercial" value={f.nombreComercial} onChange={pon("nombreComercial")} placeholder="Autos del Norte" className="text-base sm:text-sm" />
            </Campo>
            <Campo etiqueta="RFC" htmlFor="rfc" error={!rfcValido ? "Formato de RFC no válido." : rfcCoincide ? "Coincide con una cuenta existente; usa esa cuenta." : undefined} ayuda={datos.rfc && rfcValido ? "Formato válido" : "Opcional; 12 o 13 caracteres."}>
              <Input id="rfc" value={f.rfc} onChange={pon("rfc")} placeholder="ANS840212KQ3" className="uppercase text-base sm:text-sm" maxLength={13} aria-invalid={!rfcValido || rfcCoincide} />
            </Campo>
            <Campo etiqueta="Giro" htmlFor="giro">
              <Input id="giro" value={f.segmento} onChange={pon("segmento")} placeholder="Concesionaria automotriz" className="text-base sm:text-sm" />
            </Campo>
            <Campo etiqueta="Origen del lead" htmlFor="origen">
              <Input id="origen" value={f.origen} onChange={pon("origen")} placeholder="Referido, formulario web, evento…" className="text-base sm:text-sm" list="origenes" />
              <datalist id="origenes">
                {["Referido", "Formulario web", "Llamada entrante", "Evento", "Cartera", "Redes sociales"].map((o) => (
                  <option key={o} value={o} />
                ))}
              </datalist>
            </Campo>
            <Campo etiqueta="Teléfono" htmlFor="tel">
              <Input id="tel" type="tel" value={f.telefono} onChange={pon("telefono")} placeholder="844 410 22 10" className="text-base sm:text-sm" />
            </Campo>
            <Campo etiqueta="Correo" htmlFor="correo">
              <Input id="correo" type="email" value={f.correo} onChange={pon("correo")} placeholder="contacto@empresa.com" className="text-base sm:text-sm" />
            </Campo>
            <Campo etiqueta="Ciudad o domicilio" htmlFor="dom" className="sm:col-span-2">
              <Input id="dom" value={f.domicilio} onChange={pon("domicilio")} placeholder="Saltillo, Coahuila" className="text-base sm:text-sm" />
            </Campo>
          </div>

          <fieldset className="space-y-4">
            <legend className="font-titulo text-base font-bold">Contacto principal</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo etiqueta="Nombre" htmlFor="cnombre">
                <Input id="cnombre" value={f.contactoNombre} onChange={pon("contactoNombre")} placeholder="Ana Ruiz" className="text-base sm:text-sm" />
              </Campo>
              <Campo etiqueta="Puesto" htmlFor="cpuesto">
                <Input id="cpuesto" value={f.contactoPuesto} onChange={pon("contactoPuesto")} placeholder="Gerente de mercadotecnia" className="text-base sm:text-sm" />
              </Campo>
              <Campo etiqueta="Celular" htmlFor="ctel">
                <Input id="ctel" type="tel" value={f.contactoTelefono} onChange={pon("contactoTelefono")} placeholder="10 dígitos" className="text-base sm:text-sm" />
              </Campo>
              <Campo etiqueta="Correo" htmlFor="ccorreo">
                <Input id="ccorreo" type="email" value={f.contactoCorreo} onChange={pon("contactoCorreo")} placeholder="ana@empresa.com" className="text-base sm:text-sm" />
              </Campo>
            </div>
          </fieldset>

          <ErrorEnLinea error={error} />
          <div className="flex flex-wrap items-center gap-3">
            <Boton type="submit" variante="acento" disabled={bloqueado || guardar.isPending}>
              {guardar.isPending ? "Guardando…" : "Guardar cuenta"}
            </Boton>
            <Boton type="button" variante="contorno" asChild>
              <Link to="/cuentas">Cancelar</Link>
            </Boton>
            <span className="font-ui text-xs text-tinta-tenue" aria-live="polite">
              {estadoGuardar}
            </span>
          </div>
        </form>

        <aside id="parecidos" className="scroll-mt-4 lg:sticky lg:top-6 lg:self-start" aria-live="polite">
          <Kicker>Parecidos encontrados · se actualiza mientras escribes</Kicker>
          <div className="mt-2 border border-linea bg-fondo-suave p-4">
            {!buscar ? (
              <p className="font-ui text-sm text-tinta-tenue">Escribe la razón social, el RFC, el teléfono o el correo y comparamos contra las cuentas de esta plataforma y de Odoo.</p>
            ) : parecidos.isLoading ? (
              <p className="font-ui text-sm text-tinta-tenue">Comparando…</p>
            ) : lista.length === 0 ? (
              <>
                <h2 className="font-titulo text-lg font-bold">Ninguna cuenta se parece a esta</h2>
                <p className="mt-1 font-ui text-sm text-tinta-tenue">Comparamos razón social, RFC, teléfono y correo{parecidos.data?.conEmbeddings ? " y el parecido semántico" : ""}.</p>
              </>
            ) : (
              <>
                <h2 className="font-titulo text-lg font-bold">
                  {lista.length} cuenta{lista.length > 1 ? "s" : ""} se parece{lista.length > 1 ? "n" : ""} a esta
                </h2>
                <p className="mt-1 font-ui text-sm text-tinta-tenue">Comparamos razón social, RFC, teléfono y correo contra Odoo y esta plataforma.</p>
                <ul className="mt-4 divide-y divide-linea">
                  {lista.map((p) => (
                    <li key={p.id} className="py-3">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-titulo text-[15px] font-semibold">{p.cuenta.razonSocial}</div>
                        <span className="font-titulo text-sm font-bold tabular-nums">{Math.round(p.puntaje * 100)} %</span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {p.motivos.map((m) => (
                          <Pill key={m} tono={p.nivel === "seguro" ? "tinta" : "neutro"}>
                            {m}
                          </Pill>
                        ))}
                      </div>
                      <div className="mt-1 font-ui text-xs text-tinta-tenue">
                        {p.cuenta.ejecutivo ?? "Sin ejecutivo"} · {p.cuenta.estado}
                        {p.cuenta.ultimaActividadEn ? ` · última actividad ${relativo(p.cuenta.ultimaActividadEn)}` : ""}
                      </div>
                      <div className="mt-2 flex gap-2">
                        <Boton tamano="sm" variante="primario" asChild>
                          <Link to="/cuentas/$id" params={{ id: p.cuenta.id }}>
                            Usar esta cuenta
                          </Link>
                        </Boton>
                        <Boton tamano="sm" variante="liga" asChild>
                          <Link to="/cuentas/$id" params={{ id: p.cuenta.id }} target="_blank" rel="noreferrer">
                            Ver ficha
                          </Link>
                        </Boton>
                      </div>
                    </li>
                  ))}
                </ul>
                {bloquean.length > 0 && (
                  <div className="mt-4 border-t border-linea-fuerte pt-4">
                    <h3 className="font-titulo text-base font-bold">¿Es una empresa distinta?</h3>
                    <p className="mt-1 font-ui text-xs text-tinta-tenue">Si continúas, la cuenta queda marcada «revisada como distinta» con tu nombre y fecha. El subgerente recibe el aviso en su resumen semanal.</p>
                    <Boton className="mt-3" variante={decision === "distinta" ? "secundario" : "contorno"} tamano="sm" onClick={() => setDecision("distinta")} aria-pressed={decision === "distinta"}>
                      {decision === "distinta" ? "Marcada como distinta" : "Es distinta, continuar"}
                    </Boton>
                  </div>
                )}
              </>
            )}
          </div>
        </aside>
      </div>
      {/* Celular: el panel de parecidos queda abajo del formulario; esta barra resume su estado y trae la acción a la mano. */}
      <div className="fixed inset-x-0 bottom-0 z-10 flex items-center gap-3 border-t border-linea-fuerte bg-fondo px-4 py-2 lg:hidden">
        <div className="min-w-0 flex-1 font-ui text-xs text-tinta-3">
          {lista.length > 0 ? (
            <a href="#parecidos" className="font-semibold text-azul underline-offset-2 hover:underline">
              {lista.length} parecido{lista.length > 1 ? "s" : ""} · ver
            </a>
          ) : (
            <span>{buscar ? "Sin parecidos" : "Escribe la razón social"}</span>
          )}
          {estadoGuardar && <div className="truncate">{estadoGuardar}</div>}
        </div>
        <Boton variante="acento" tamano="sm" disabled={bloqueado || guardar.isPending} onClick={() => guardar.mutate()}>
          {guardar.isPending ? "Guardando…" : "Guardar"}
        </Boton>
      </div>
    </>
  );
}
